import TextArea from "../../../../../Components/Common/TextArea/TextArea";
import React from "react";
import Button from "../../../../../Components/Common/Button/Button";
import Input from "../../../../../Components/Common/Input/Input";
import PhoneInput from "../../../../../Components/Common/PhoneInput/PhoneInput";
import SearchableSelect from "../../../../../Components/Common/SearchableSelect/SearchableSelect";
import {
  useUsersByRole,
  UserOption,
} from "../../../../../hooks/useUsersByRole";
import ErrorMessage from "../../../../../Components/Common/ErrorMessage/ErrorMessage";
import {
  CHAR_LIMIT,
  CHAR_MIN,
  EMAIL_REGEX,
} from "../../../../../utils/Common/Constant";
import { NewProjectFormValues } from "../types";
import { FormikErrors, FormikTouched } from "formik";

interface Step3ContactsProps {
  values: NewProjectFormValues;
  errors: FormikErrors<NewProjectFormValues>;
  touched: FormikTouched<NewProjectFormValues>;
  setFieldValue: (name: string, value: string) => void;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  onNext: () => void;
  onBack: () => void;
  /** Admin only: send the user to Manage Users to create a missing contact. */
  onCreateUser?: (role: "owner" | "gc" | "architect") => void;
}

export default function Step3Contacts({
  values,
  errors,
  touched,
  setFieldValue,
  onChange,
  onNext,
  onBack,
  onCreateUser,
}: Step3ContactsProps) {
  const owner = useUsersByRole("owner");
  const gc = useUsersByRole("gc");
  const architect = useUsersByRole("architect");

  const fieldHasError = (
    value: string,
    have?: number,
    min?: number,
    touched?: boolean,
    error?: string,
    regex?: RegExp,
  ): boolean => {
    const len = value.length;
    const isPhoneEmpty =
      value.startsWith("+") && value.replace(/\D/g, "").length <= 3;
    if (have !== undefined && len > have) return true;
    if (min !== undefined && !isPhoneEmpty && len > 0 && len < min) return true;
    if (regex && len > 0 && !regex.test(value)) return true;
    if (touched && !!error) return true;
    return false;
  };

  return (
    <div className="stepPanel active">
      <div className="card">
        <div className="cardTitle">People &amp; Contacts</div>
        <div className="sectionLabel">Owner</div>
        <div className="fieldGroup cols2">
          <div className="field">
            <label>
              Owner Name <span className="req">*</span>
            </label>
            <SearchableSelect<UserOption>
              id="ownerId"
              value={values.ownerId}
              valueLabel={
                values.ownerId && values.ownerName
                  ? `${values.ownerName} (${values.ownerEmail ?? ""})`
                  : ""
              }
              options={owner.options}
              getOptionValue={(u) => u.id}
              getOptionLabel={(u) =>
                `${u.firstName} ${u.lastName} (${u.email})`
              }
              onCreateNew={onCreateUser && (() => onCreateUser("owner"))}
              createNewLabel="+ Add new owner"
              placeholder={owner.loading ? "Loading..." : "Select owner..."}
              error={Boolean(touched.ownerId && errors.ownerId)}
              apiSearch
              onSearch={owner.onSearch}
              hasMore={owner.hasMore && !owner.loading}
              onLoadMore={owner.onLoadMore}
              onChange={(id, user) => {
                setFieldValue("ownerId", id);
                setFieldValue(
                  "ownerName",
                  user ? `${user.firstName} ${user.lastName}` : "",
                );
                setFieldValue(
                  "ownerPrimaryContact",
                  user?.primaryContact ?? "",
                );
                setFieldValue("ownerPhone", user?.phone ?? "");
                setFieldValue("ownerEmail", user?.email ?? "");
                setFieldValue("ownerAddr", user?.address ?? "");
              }}
            />
            {touched.ownerId && errors.ownerId && (
              <span className="field-error">{errors.ownerId}</span>
            )}
          </div>
          <div className="field">
            <label>Primary Contact</label>
            <Input
              type="text"
              id="ownerPrimaryContact"
              name="ownerPrimaryContact"
              value={values.ownerPrimaryContact}
              onChange={onChange}
              className={
                fieldHasError(
                  values.ownerPrimaryContact,
                  CHAR_LIMIT.NAME,
                  undefined,
                  !!touched.ownerPrimaryContact,
                  errors.ownerPrimaryContact,
                )
                  ? "error-input-red"
                  : ""
              }
            />
            <ErrorMessage
              value={values.ownerPrimaryContact}
              have={CHAR_LIMIT.NAME}
              touched={touched.ownerPrimaryContact}
              error={errors.ownerPrimaryContact}
            />
          </div>
        </div>
        <div className="fieldGroup cols3">
          <div className="field">
            <label>Phone</label>
            <PhoneInput
              value={values.ownerPhone}
              onChange={() => {}}
              disabled
            />
          </div>
          <div className="field">
            <label>Email</label>
            <Input
              type="email"
              id="ownerEmail"
              name="ownerEmail"
              value={values.ownerEmail}
              onChange={() => {}}
              readOnly
              placeholder="Auto-filled from owner selection"
            />
          </div>
          <div className="field">
            <label>Address</label>
            <Input
              type="text"
              id="ownerAddr"
              name="ownerAddr"
              value={values.ownerAddr}
              onChange={() => {}}
              readOnly
              placeholder="Auto-filled from owner selection"
            />
          </div>
        </div>

        <div className="sectionLabel">Requisition Signer</div>
        <div className="fieldGroup cols1">
          <div className="field">
            <label>
              Who needs to sign the requisition? <span className="req">*</span>
            </label>
            <Input
              type="text"
              id="reqSigner"
              name="reqSigner"
              value={values.reqSigner}
              onChange={onChange}
              className={
                fieldHasError(
                  values.reqSigner,
                  CHAR_LIMIT.NAME,
                  undefined,
                  !!touched.reqSigner,
                  errors.reqSigner,
                )
                  ? "error-input-red"
                  : ""
              }
            />
            <ErrorMessage
              value={values.reqSigner}
              have={CHAR_LIMIT.NAME}
              touched={touched.reqSigner}
              error={errors.reqSigner}
            />
          </div>
        </div>
        <div className="fieldGroup cols3">
          <div className="field">
            <label>Phone</label>
            <PhoneInput
              value={values.reqPhone}
              onChange={(value) => setFieldValue("reqPhone", value)}
              error={fieldHasError(
                values.reqPhone,
                undefined,
                CHAR_MIN.CONTACT,
                !!touched.reqPhone,
                errors.reqPhone,
              )}
            />
            <ErrorMessage
              value={values.reqPhone}
              min={CHAR_MIN.CONTACT}
              have={CHAR_LIMIT.PHONE}
              touched={touched.reqPhone}
              error={errors.reqPhone}
            />
          </div>
          <div className="field">
            <label>Email</label>
            <Input
              type="email"
              id="reqEmail"
              name="reqEmail"
              value={values.reqEmail}
              onChange={onChange}
              className={
                fieldHasError(
                  values.reqEmail,
                  CHAR_LIMIT.EMAIL,
                  undefined,
                  !!touched.reqEmail,
                  errors.reqEmail,
                  EMAIL_REGEX,
                )
                  ? "error-input-red"
                  : ""
              }
            />
            <ErrorMessage
              value={values.reqEmail}
              have={CHAR_LIMIT.EMAIL}
              touched={touched.reqEmail}
              error={errors.reqEmail}
            />
            {values.reqEmail.length > 0 &&
              values.reqEmail.length <= CHAR_LIMIT.EMAIL &&
              !EMAIL_REGEX.test(values.reqEmail) && (
                <span className="field-error">Invalid email address</span>
              )}
          </div>
          <div className="field">
            <label>Address</label>
            <Input
              type="text"
              id="reqAddr"
              name="reqAddr"
              value={values.reqAddr}
              onChange={onChange}
              className={
                fieldHasError(
                  values.reqAddr,
                  CHAR_LIMIT.ADDRESS,
                  undefined,
                  !!touched.reqAddr,
                  errors.reqAddr,
                )
                  ? "error-input-red"
                  : ""
              }
            />
            <ErrorMessage
              value={values.reqAddr}
              have={CHAR_LIMIT.ADDRESS}
              touched={touched.reqAddr}
              error={errors.reqAddr}
            />
          </div>
        </div>

        <div className="sectionLabel">General Contractor</div>
        <div className="fieldGroup cols2">
          <div className="field">
            <label>
              General Contractor <span className="req">*</span>
            </label>
            <SearchableSelect<UserOption>
              id="gcId"
              value={values.gcId}
              valueLabel={
                values.gcId && values.gcName
                  ? `${values.gcName} (${values.gcEmail ?? ""})`
                  : ""
              }
              options={gc.options}
              getOptionValue={(u) => u.id}
              getOptionLabel={(u) =>
                `${u.firstName} ${u.lastName} (${u.email})`
              }
              onCreateNew={onCreateUser && (() => onCreateUser("gc"))}
              createNewLabel="+ Add new general contractor"
              placeholder={gc.loading ? "Loading..." : "Select GC..."}
              error={Boolean(touched.gcId && errors.gcId)}
              apiSearch
              onSearch={gc.onSearch}
              hasMore={gc.hasMore && !gc.loading}
              onLoadMore={gc.onLoadMore}
              onChange={(id, user) => {
                setFieldValue("gcId", id);
                setFieldValue(
                  "gcName",
                  user ? `${user.firstName} ${user.lastName}` : "",
                );
                setFieldValue("gcPrimaryContact", user?.primaryContact ?? "");
                setFieldValue("gcPhone", user?.phone ?? "");
                setFieldValue("gcEmail", user?.email ?? "");
                setFieldValue("gcAddr", user?.address ?? "");
              }}
            />
            {touched.gcId && errors.gcId && (
              <span className="field-error">{errors.gcId}</span>
            )}
          </div>
          <div className="field">
            <label>Primary Contact</label>
            <Input
              type="text"
              id="gcPrimaryContact"
              name="gcPrimaryContact"
              value={values.gcPrimaryContact}
              onChange={onChange}
              className={
                fieldHasError(
                  values.gcPrimaryContact,
                  CHAR_LIMIT.NAME,
                  undefined,
                  !!touched.gcPrimaryContact,
                  errors.gcPrimaryContact,
                )
                  ? "error-input-red"
                  : ""
              }
            />
            <ErrorMessage
              value={values.gcPrimaryContact}
              have={CHAR_LIMIT.NAME}
              touched={touched.gcPrimaryContact}
              error={errors.gcPrimaryContact}
            />
          </div>
        </div>
        <div className="fieldGroup cols3">
          <div className="field">
            <label>Phone</label>
            <PhoneInput value={values.gcPhone} onChange={() => {}} disabled />
          </div>
          <div className="field">
            <label>Email</label>
            <Input
              type="email"
              id="gcEmail"
              name="gcEmail"
              value={values.gcEmail}
              onChange={() => {}}
              readOnly
              placeholder="Auto-filled from GC selection"
            />
          </div>
          <div className="field">
            <label>Address</label>
            <Input
              type="text"
              id="gcAddr"
              name="gcAddr"
              value={values.gcAddr}
              onChange={() => {}}
              readOnly
              placeholder="Auto-filled from GC selection"
            />
          </div>
        </div>

        <div className="sectionLabel">Other Parties</div>
        <div className="fieldGroup cols2">
          <div className="field">
            <label>Architect</label>
            <SearchableSelect<UserOption>
              id="architect"
              value={values.architect}
              valueLabel={
                values.architect && values.architectName
                  ? values.architectName
                  : ""
              }
              options={architect.options}
              getOptionValue={(u) => u.id}
              getOptionLabel={(u) =>
                `${u.firstName} ${u.lastName} (${u.email})`
              }
              onCreateNew={onCreateUser && (() => onCreateUser("architect"))}
              createNewLabel="+ Add new architect"
              placeholder={
                architect.loading ? "Loading..." : "Select architect..."
              }
              apiSearch
              onSearch={architect.onSearch}
              hasMore={architect.hasMore && !architect.loading}
              onLoadMore={architect.onLoadMore}
              onChange={(id, user) => {
                setFieldValue("architect", id);
                setFieldValue(
                  "architectName",
                  user ? `${user.firstName} ${user.lastName}` : "",
                );
              }}
            />
          </div>
          <div className="field">
            <label>Owner's Representative</label>
            <Input
              type="text"
              id="ownerRep"
              name="ownerRep"
              value={values.ownerRep}
              onChange={onChange}
              className={
                fieldHasError(
                  values.ownerRep,
                  CHAR_LIMIT.NAME,
                  undefined,
                  !!touched.ownerRep,
                  errors.ownerRep,
                )
                  ? "error-input-red"
                  : ""
              }
            />
            <ErrorMessage
              value={values.ownerRep}
              have={CHAR_LIMIT.NAME}
              touched={touched.ownerRep}
              error={errors.ownerRep}
            />
          </div>
        </div>
        <div className="fieldGroup cols1">
          <div className="field">
            <label>Other Project Entities</label>
            <TextArea
              id="otherEntities"
              name="otherEntities"
              value={values.otherEntities}
              onChange={onChange}
              placeholder="List entities, one per line…"
              className={
                fieldHasError(
                  values.otherEntities,
                  CHAR_LIMIT.DESCRIPTION,
                  undefined,
                  !!touched.otherEntities,
                  errors.otherEntities,
                )
                  ? "error-input-red"
                  : ""
              }
              maxChars={CHAR_LIMIT.DESCRIPTION}
            />
            <ErrorMessage
              value={values.otherEntities}
              touched={touched.otherEntities}
              error={errors.otherEntities}
            />
          </div>
        </div>
      </div>
      <div className="btnRow">
        <Button className="btn btnSecondary" onClick={onBack}>
          ← Back
        </Button>
        <Button className="btn btnPrimary" onClick={onNext}>
          Next: Lender →
        </Button>
      </div>
    </div>
  );
}
